import React, { useEffect } from 'react';
import './participant/screens/molecules.css';

export type JoinStatus = 'joining' | 'success' | 'error' | 'already-joined';

interface JoinProjectProps {
    projectId: string;
    projectName: string | null;
    status: JoinStatus;
    onJoinComplete: () => void;
}

const JoinProject: React.FC<JoinProjectProps> = ({ projectId, projectName, status, onJoinComplete }) => {

    useEffect(() => {
        if (status === 'success' || status === 'already-joined') {
            const timer = setTimeout(() => {
                onJoinComplete();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [status, onJoinComplete]);

    const bgColor =
        status === 'error' ? '#E8A09A' :
        status === 'success' || status === 'already-joined' ? '#6E7A5A' :
        '#7BBFAF';

    const getIcon = () => {
        switch (status) {
            case 'joining':
                return (
                    <div className="w-[72px] h-[72px] bg-white/25 rounded-[20px] flex items-center justify-center">
                        <div className="w-8 h-8 border-[3px] border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                );
            case 'success':
            case 'already-joined':
                return (
                    <div className="w-[72px] h-[72px] bg-white/25 rounded-[20px] flex items-center justify-center">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                );
            case 'error':
                return (
                    <div className="w-[72px] h-[72px] bg-white/25 rounded-[20px] flex items-center justify-center">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="#1A1814" strokeWidth="1.5"/>
                            <path d="M15 9l-6 6M9 9l6 6" stroke="#1A1814" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                );
        }
    };

    const getTitle = () => {
        switch (status) {
            case 'joining': return 'Joining project...';
            case 'success': return 'Welcome!';
            case 'already-joined': return 'Already a member';
            case 'error': return 'Something went wrong';
        }
    };

    const getMessage = () => {
        switch (status) {
            case 'joining': return 'Setting up your account...';
            case 'success': return 'Successfully joined the project!';
            case 'already-joined': return 'You are already a member of this project.';
            case 'error': return 'Failed to join project. Please try again.';
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden flex items-center justify-center" style={{ backgroundColor: bgColor }}>
            {/* Top-left — diagonal pair */}
            <div className="mol" style={{ position: 'absolute', top: 60, left: 40, pointerEvents: 'none' }}>
                <svg width="75" height="45" viewBox="-32 -10 85 50" fill="none">
                    <circle cx="0" cy="0" r="3.5" fill="#fff"/>
                    <circle cx="25" cy="18" r="3" fill="#fff"/>
                    <line x1="3" y1="2" x2="22" y2="16" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M-4,0 Q-12,-8 -20,0 Q-28,-8 -36,0" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Top-right — triangle */}
            <div className="mol-d1" style={{ position: 'absolute', top: 75, right: 35, pointerEvents: 'none' }}>
                <svg width="68" height="45" viewBox="-5 -10 72 50" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="30" cy="0" r="3" fill="#fff"/>
                    <circle cx="15" cy="26" r="3.5" fill="#fff"/>
                    <line x1="3" y1="0" x2="27" y2="0" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="1" y1="3" x2="13" y2="23" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="29" y1="3" x2="17" y2="23" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M34,0 Q42,-8 50,0 Q58,-8 66,0" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Mid-left — vertical pair */}
            <div className="mol-d3" style={{ position: 'absolute', top: '42%', left: 30, pointerEvents: 'none' }}>
                <svg width="42" height="45" viewBox="-8 -12 48 50" fill="none">
                    <circle cx="5" cy="0" r="3" fill="#fff"/>
                    <circle cx="5" cy="28" r="3.5" fill="#fff"/>
                    <line x1="5" y1="3" x2="5" y2="25" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M9,-2 Q17,-10 25,-2 Q33,-10 41,-2" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Mid-right — single node with squiggle */}
            <div className="mol-d4" style={{ position: 'absolute', top: '52%', right: 35, pointerEvents: 'none' }}>
                <svg width="52" height="18" viewBox="-5 -9 58 18" fill="none">
                    <circle cx="0" cy="0" r="3.5" fill="#fff"/>
                    <path d="M5,0 Q13,-7 21,0 Q29,-7 37,0 Q45,-7 53,0" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Bottom-left — L-shape */}
            <div className="mol-d5" style={{ position: 'absolute', bottom: 95, left: 45, pointerEvents: 'none' }}>
                <svg width="60" height="42" viewBox="-28 -12 70 46" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="0" cy="22" r="3.5" fill="#fff"/>
                    <circle cx="25" cy="22" r="3" fill="#fff"/>
                    <line x1="0" y1="3" x2="0" y2="19" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="3" y1="22" x2="22" y2="22" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M-4,-2 Q-12,-10 -20,-2 Q-28,-10 -36,-2" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Center-left upper — angled pair */}
            <div className="mol-d6" style={{ position: 'absolute', top: '28%', left: '14%', pointerEvents: 'none' }}>
                <svg width="68" height="32" viewBox="-8 -8 72 36" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="26" cy="12" r="3.5" fill="#fff"/>
                    <line x1="3" y1="1" x2="23" y2="10" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M30,15 Q38,22 46,15 Q54,22 62,15" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Center-right upper — V-shape */}
            <div className="mol-d7" style={{ position: 'absolute', top: '30%', right: '12%', pointerEvents: 'none' }}>
                <svg width="55" height="35" viewBox="-28 -12 62 38" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="16" cy="20" r="3.5" fill="#fff"/>
                    <circle cx="32" cy="0" r="3" fill="#fff"/>
                    <line x1="2" y1="2" x2="14" y2="18" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="30" y1="2" x2="18" y2="18" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M-4,-2 Q-12,-10 -20,-2 Q-28,-10 -36,-2" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Center-left lower — single node */}
            <div className="mol-d8" style={{ position: 'absolute', top: '63%', left: '16%', pointerEvents: 'none' }}>
                <svg width="42" height="16" viewBox="-5 -8 48 16" fill="none">
                    <circle cx="0" cy="0" r="3.5" fill="#fff"/>
                    <path d="M5,0 Q13,-7 21,0 Q29,-7 37,0" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Center-right lower — horizontal triple */}
            <div className="mol-d9" style={{ position: 'absolute', top: '67%', right: '13%', pointerEvents: 'none' }}>
                <svg width="70" height="18" viewBox="-5 -9 76 18" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="18" cy="0" r="2.5" fill="#fff"/>
                    <circle cx="36" cy="0" r="3" fill="#fff"/>
                    <line x1="3" y1="0" x2="15" y2="0" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="21" y1="0" x2="33" y2="0" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M40,0 Q48,-7 56,0 Q64,-7 72,0" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Bottom-right — horizontal pair */}
            <div className="mol-d2" style={{ position: 'absolute', bottom: 55, right: 40, pointerEvents: 'none' }}>
                <svg width="68" height="20" viewBox="-10 -10 78 20" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="30" cy="0" r="3.5" fill="#fff"/>
                    <line x1="3" y1="0" x2="27" y2="0" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M34,0 Q42,-8 50,0 Q58,-8 66,0" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Top-center — Cluster E (2-node minimal) */}
            <div className="mol-d10" style={{ position: 'absolute', top: '12%', left: '43%', pointerEvents: 'none' }}>
                <svg width="68" height="22" viewBox="-5 -14 68 22" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="22" cy="-5" r="2.5" fill="#fff"/>
                    <line x1="3" y1="-1" x2="20" y2="-4" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M25,-8 Q30,-13 34,-7 Q39,-14 44,-8 Q49,-14 54,-8" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Upper-center — Cluster D (3-node diagonal) */}
            <div className="mol-d8" style={{ position: 'absolute', top: '22%', left: '49%', pointerEvents: 'none' }}>
                <svg width="58" height="35" viewBox="-20 -5 68 35" fill="none">
                    <circle cx="0" cy="0" r="3.5" fill="#fff"/>
                    <circle cx="20" cy="12" r="2.5" fill="#fff"/>
                    <circle cx="-12" cy="22" r="3" fill="#fff"/>
                    <line x1="3" y1="2" x2="18" y2="11" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="-2" y1="3" x2="-11" y2="20" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M23,15 Q28,10 31,16 Q35,10 39,17 Q43,10 47,17" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                    <path d="M-16,25 Q-22,31 -28,25 Q-34,31 -40,25" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Lower-center — Cluster C (2-node compact) */}
            <div className="mol-d6" style={{ position: 'absolute', top: '73%', left: '45%', pointerEvents: 'none' }}>
                <svg width="60" height="24" viewBox="-28 -15 68 24" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="18" cy="-7" r="2.5" fill="#fff"/>
                    <line x1="3" y1="-2" x2="16" y2="-6" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M21,-10 Q26,-15 30,-9 Q34,-16 39,-10" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                    <path d="M-4,3 Q-9,8 -13,3 Q-18,9 -23,3 Q-28,9 -33,3" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>
            {/* Bottom-center — Cluster F (5-node dense) */}
            <div className="mol-d9" style={{ position: 'absolute', bottom: '10%', left: '39%', pointerEvents: 'none' }}>
                <svg width="70" height="40" viewBox="-8 -16 70 42" fill="none">
                    <circle cx="0" cy="0" r="3" fill="#fff"/>
                    <circle cx="20" cy="-6" r="2.5" fill="#fff"/>
                    <circle cx="32" cy="8" r="2" fill="#fff"/>
                    <circle cx="16" cy="18" r="2.5" fill="#fff"/>
                    <circle cx="-4" cy="16" r="2" fill="#fff"/>
                    <line x1="3" y1="-2" x2="18" y2="-5" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="22" y1="-4" x2="30" y2="6" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="30" y1="10" x2="18" y2="17" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="14" y1="18" x2="-2" y2="17" stroke="#fff" strokeWidth="1.5"/>
                    <line x1="-3" y1="14" x2="-1" y2="3" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M35,11 Q40,6 43,12 Q48,6 53,12 Q58,6 63,12" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                    <path d="M23,-10 Q28,-16 33,-10 Q38,-16 43,-10" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center px-7 max-w-[360px]">
                <div className="mb-6">{getIcon()}</div>

                <h1 className="text-[#1A1814] text-[28px] font-semibold leading-tight">
                    {getTitle()}
                </h1>

                {projectName && (
                    <p className="text-[#1A1814]/80 text-base font-semibold mt-3">
                        {projectName}
                    </p>
                )}

                <p className="text-[#1A1814]/60 text-[15px] mt-3.5 leading-relaxed">
                    {getMessage()}
                </p>

                {status === 'joining' && (
                    <div className="flex gap-1.5 mt-8">
                        {[0, 1, 2].map(i => (
                            <div
                                key={i}
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                    backgroundColor: 'rgba(26,24,20,0.3)',
                                    animation: `jpPulse 1s ease-in-out ${i * 0.2}s infinite`,
                                }}
                            />
                        ))}
                    </div>
                )}

                {status === 'error' && (
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-8 bg-[#1A1814] text-white rounded-full py-4 px-10 text-[15px] font-semibold tracking-[1px]"
                    >
                        TRY AGAIN
                    </button>
                )}
            </div>

            <style>{`@keyframes jpPulse { 0%,100% { opacity:.3; transform:scale(.8) } 50% { opacity:1; transform:scale(1) } }`}</style>
        </div>
    );
};

export default JoinProject;
